// The account's theme (spec web/appearance-tab): saved on the server with the person's unlocks,
// shown from this browser's copy (the `theme` pref) before the first render. A team page reads the
// account's and follows it, so a theme chosen on another device follows the person here. Every
// team's read carries the account's preference, so a choice goes into every read in the cache.
import { useIsMutating, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  getGetAppearanceQueryKey,
  getUpdateMyAppearanceMutationKey,
  useGetAppearance,
  useUpdateMyAppearance,
} from "../../api/generated/endpoints/appearance/appearance";
import type { AppearanceView, ThemeId } from "../../api/generated/model";
import { readPref } from "../prefs";
import { showTheme } from "./current";

/**
 * Shows the account's saved theme once the team's appearance is read. It is mounted after the team's
 * media grant, as every read of the team waits for it (spec assets/media-cookies, Design 4).
 */
export function useAccountTheme(teamId: string): void {
  const saved = useGetAppearance(teamId, { query: { select: (view) => view.theme } }).data;
  // While a write of the account's is on its way, the newest choice is shown: a read meanwhile can
  // be older. The count is watched, so the last write's end brings the follower back to what the
  // account saved, even when the read did not change again (spec web/case-switches, decision 6).
  const writing = useIsMutating({ mutationKey: getUpdateMyAppearanceMutationKey() }) > 0;
  useEffect(() => {
    if (!writing && saved !== undefined && saved !== readPref("theme")) showTheme(saved);
  }, [saved, writing]);
}

/** Every team's appearance read, matched from the generated key: each holds the account's preference. */
const [READ] = getGetAppearanceQueryKey("\u0000");
const [READ_HEAD = "", READ_TAIL = ""] = READ.split("\u0000");
export const appearanceReads = {
  predicate: ({ queryKey: [path] }: { queryKey: QueryKey }) =>
    typeof path === "string" &&
    path.startsWith(READ_HEAD) &&
    path.endsWith(READ_TAIL) &&
    !path.slice(READ_HEAD.length, path.length - READ_TAIL.length).includes("/"),
};

type Preference = Partial<Pick<AppearanceView, "theme" | "cases_hidden">>;

/** The account's preference into every cached team read; each team's own fields stay as they are. */
function setPreference(queryClient: QueryClient, preference: Preference): void {
  queryClient.setQueriesData<AppearanceView>(appearanceReads, (view) =>
    view === undefined ? view : { ...view, ...preference },
  );
}

/** The account's writes, one at a time and in order (TanStack's mutation scope, held by the cache). */
const ACCOUNT_WRITES = { id: "account-appearance" };

/** The newest choice this tab has made, page or no page: only its answer changes what is shown. */
let newest = 0;

/** The case's switch owes every read a refresh, made once the account's newest write is done. */
let refreshOwed = false;

interface Choice {
  /** This choice's place among the tab's choices. */
  seq: number;
  /** What was shown before it, and every team's read. */
  shown: string;
  views: [QueryKey, AppearanceView | undefined][];
}

/**
 * Choosing a theme: shown at once and saved to the account. The saves reach the server one at a
 * time and in order, so it keeps the newest choice, and only the newest choice's answer touches what
 * is shown: an older save that answers late, or is refused, cannot bring its theme back, even after
 * the page is left (spec web/appearance-tab, decision 4). A refused newest choice puts back what was
 * shown before it and reads the account again, which says what the saves kept. Every team's read in
 * the cache takes the choice at once, and the saved preference after (decision 5): the account has
 * one theme, and a team read moments ago must not bring the old one back. The callbacks
 * are the mutation's own, which run even after the page is left (TanStack skips those given to
 * `mutate` then; spec web/account-settings, decision 7).
 */
export function useThemeChoice() {
  const queryClient = useQueryClient();
  const update = useUpdateMyAppearance<unknown, Choice>({
    mutation: {
      scope: ACCOUNT_WRITES,
      onMutate: async ({ data }) => {
        newest += 1;
        const choice: Choice = {
          seq: newest,
          shown: readPref("theme"),
          views: queryClient.getQueriesData<AppearanceView>(appearanceReads),
        };
        await queryClient.cancelQueries(appearanceReads);
        if (data.theme !== undefined && data.theme !== null && choice.seq === newest) {
          const theme = data.theme;
          showTheme(theme);
          setPreference(queryClient, { theme });
        }
        return choice;
      },
      onError: (_error, _variables, choice) => {
        if (choice?.seq !== newest) return;
        showTheme(choice.shown);
        for (const [key, view] of choice.views) queryClient.setQueryData(key, view);
        // What was shown before may be a choice whose own save was refused: the account says.
        void queryClient.invalidateQueries(appearanceReads);
      },
      onSuccess: (saved, _variables, choice) => {
        if (choice?.seq !== newest) return;
        setPreference(queryClient, { theme: saved.theme, cases_hidden: saved.cases_hidden });
      },
      // After the case's switch, the account's, the server says what each team's case is now. A newer
      // write still on its way makes that refresh when it is done, so no read lands between them to
      // show an older theme (spec web/case-switches, decision 4).
      onSettled: (_saved, _error, { data }, choice) => {
        if (data.cases_hidden !== undefined) refreshOwed = true;
        if (!refreshOwed || choice?.seq !== newest) return;
        refreshOwed = false;
        void queryClient.invalidateQueries(appearanceReads);
      },
    },
  });
  const choose = (theme: ThemeId) => update.mutate({ data: { theme } });
  /** The person's own switch for the free daily case (spec web/case-switches). */
  const setCasesHidden = (hidden: boolean) => update.mutate({ data: { cases_hidden: hidden } });
  return { choose, setCasesHidden, update };
}
